import { z } from 'zod'
import { slugifyTag } from './tags.ts'

/**
 * Custom field definitions and value coercion.
 *
 * The wire shape for a value is deliberately one loose union
 * (`string | number | boolean | string[] | null`) rather than a per-type
 * envelope: forms produce strings, JSON produces natives, and CSV import
 * produces whatever the church typed. Everything funnels through
 * `parseFieldValue`, which is the *only* place a raw value becomes typed —
 * server and client run the identical function, so the client's inline
 * validation and the server's rejection can never disagree.
 */

export const CUSTOM_FIELD_TYPES = [
  'text',
  'number',
  'date',
  'boolean',
  'select',
  'multiselect',
  'file',
] as const
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number]

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Yes / no',
  select: 'Choose one',
  multiselect: 'Choose several',
  file: 'File',
}

/** Types whose values are option ids rather than literals. */
export const isOptionType = (type: CustomFieldType): type is 'select' | 'multiselect' =>
  type === 'select' || type === 'multiselect'

export interface FieldOption {
  id: string
  label: string
}

export interface FieldDefinition {
  id: string
  key: string
  label: string
  type: CustomFieldType
  required: boolean
  options: FieldOption[]
}

/** The JSON a client sends or receives for one value. */
export type RawFieldValue = string | number | boolean | string[] | null

/** What a parsed value is, before the repo maps it onto typed columns. */
export type NormalizedFieldValue =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'number'; number: number }
  | { kind: 'date'; date: string }
  | { kind: 'boolean'; boolean: boolean }
  | { kind: 'options'; optionIds: string[] }

export type ParseResult = { ok: true; value: NormalizedFieldValue } | { ok: false; error: string }

const MAX_TEXT = 2000
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const isEmpty = (raw: RawFieldValue): boolean =>
  raw === null ||
  raw === undefined ||
  (typeof raw === 'string' && raw.trim() === '') ||
  (Array.isArray(raw) && raw.length === 0)

export function parseFieldValue(field: FieldDefinition, raw: RawFieldValue): ParseResult {
  if (isEmpty(raw)) {
    return field.required
      ? { ok: false, error: `${field.label} is required` }
      : { ok: true, value: { kind: 'empty' } }
  }

  switch (field.type) {
    case 'text': {
      const text = String(raw).trim()
      if (text.length > MAX_TEXT) {
        return { ok: false, error: `${field.label} must be ${MAX_TEXT} characters or fewer` }
      }
      return { ok: true, value: { kind: 'text', text } }
    }

    case 'number': {
      // Form inputs hand back strings; treat "12.5" and 12.5 alike.
      const parsed = typeof raw === 'number' ? raw : Number(String(raw).trim())
      if (typeof raw === 'boolean' || Array.isArray(raw) || !Number.isFinite(parsed)) {
        return { ok: false, error: `${field.label} must be a number` }
      }
      return { ok: true, value: { kind: 'number', number: parsed } }
    }

    case 'date': {
      const date = String(raw).trim()
      if (!ISO_DATE.test(date) || Number.isNaN(new Date(`${date}T00:00:00Z`).getTime())) {
        return { ok: false, error: `${field.label} must be a date (YYYY-MM-DD)` }
      }
      return { ok: true, value: { kind: 'date', date } }
    }

    case 'boolean': {
      if (typeof raw === 'boolean') return { ok: true, value: { kind: 'boolean', boolean: raw } }
      const text = String(raw).trim().toLowerCase()
      if (['true', 'yes', '1'].includes(text)) {
        return { ok: true, value: { kind: 'boolean', boolean: true } }
      }
      if (['false', 'no', '0'].includes(text)) {
        return { ok: true, value: { kind: 'boolean', boolean: false } }
      }
      return { ok: false, error: `${field.label} must be yes or no` }
    }

    case 'file':
      // A file arrives by upload (its own two-step route), never as a value:
      // the only thing a form may say about it here is "clear it".
      return { ok: false, error: `${field.label} takes an uploaded file` }

    case 'select':
    case 'multiselect': {
      const ids = Array.isArray(raw) ? raw.map(String) : [String(raw)]
      if (field.type === 'select' && ids.length > 1) {
        return { ok: false, error: `${field.label} takes a single choice` }
      }
      const unique = [...new Set(ids)]
      const known = new Set(field.options.map((o) => o.id))
      const stranger = unique.find((id) => !known.has(id))
      if (stranger !== undefined) {
        return { ok: false, error: `${field.label} has no such choice` }
      }
      return { ok: true, value: { kind: 'options', optionIds: unique } }
    }
  }
}

/** Display string for a stored value — read-only profile views and exports. */
export function formatFieldValue(field: FieldDefinition, raw: RawFieldValue): string {
  if (isEmpty(raw)) return '—'
  switch (field.type) {
    case 'boolean':
      return raw === true || raw === 'true' ? 'Yes' : 'No'
    case 'select':
    case 'multiselect': {
      const ids = Array.isArray(raw) ? raw.map(String) : [String(raw)]
      return (
        ids
          .map((id) => field.options.find((o) => o.id === id)?.label)
          .filter((label): label is string => !!label)
          .join(', ') || '—'
      )
    }
    default:
      return String(raw)
  }
}

/** `Baptism date` → `baptism_date`: stable, greppable, CSV-header friendly. */
export function fieldKeyFromLabel(label: string): string {
  return slugifyTag(label).replace(/-/g, '_')
}

// ---- schemas ----------------------------------------------------------------

const optionInput = z.object({
  /** Present when editing an existing option, absent when adding one. */
  id: z.uuid().optional(),
  label: z.string().trim().min(1, 'Name the choice').max(100),
})

export const customFieldSchema = z
  .object({
    label: z.string().trim().min(1, 'Name the field').max(100),
    type: z.enum(CUSTOM_FIELD_TYPES),
    description: z
      .string()
      .trim()
      .max(280)
      .nullish()
      .transform((v) => (v === '' || v === undefined ? null : v)),
    required: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
    /** The profile section it shows under; null ⇒ "Custom fields". */
    sectionId: z.uuid().nullish(),
    options: z.array(optionInput).max(100).optional(),
  })
  .refine((f) => !isOptionType(f.type) || (f.options?.length ?? 0) > 0, {
    message: 'Add at least one choice',
    path: ['options'],
  })
export type CustomFieldInput = z.infer<typeof customFieldSchema>
export type CustomFieldFormInput = z.input<typeof customFieldSchema>

export const customFieldSectionSchema = z.object({
  name: z.string().trim().min(1, 'Name the section').max(60),
  sortOrder: z.number().int().min(0).max(999).optional(),
})
export type CustomFieldSectionInput = z.infer<typeof customFieldSectionSchema>

/**
 * Fields grouped for a profile: each named section in its order, then the
 * fields with no section (or a section since removed) under "Custom fields".
 */
export function groupFieldsBySection<F extends { sectionId: string | null }>(
  sections: readonly { id: string; name: string }[],
  fields: readonly F[],
): { id: string | null; name: string; fields: F[] }[] {
  const known = new Set(sections.map((section) => section.id))
  const groups = sections
    .map((section) => ({
      id: section.id as string | null,
      name: section.name,
      fields: fields.filter((field) => field.sectionId === section.id),
    }))
    .filter((group) => group.fields.length > 0)
  const loose = fields.filter((field) => field.sectionId === null || !known.has(field.sectionId))
  if (loose.length > 0) groups.push({ id: null, name: 'Custom fields', fields: loose })
  return groups
}
